import { useLatest } from 'ahooks';
import { cloneDeep } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Editor } from '@meitu/saas-editor-core';
import type { ImageEffectPlugin, IUpdateImageEffectDraftOptions } from '@meitu/saas-editor-core/es/layers/image-layer';
import { IImageLayerProps, ILayerProps } from '@meitu/saas-editor-core/es/version-types';
import { ActiveDataContext } from './active-data-context';

export const ActiveDataProvider: React.FC<{
    children: React.ReactNode;
    editor: Editor;
}> = ({ children, editor }) => {
    const originLayer = useRef<ILayerProps | null>(null);
    const [activeLayer, setActiveData] = useState<ILayerProps | null>(null);
    const [activeLayerId, setActiveDataId] = useState<string | null>(
        editor.services.frame.getActivePickLayer()?.id || null,
    );
    const latestLayer = useLatest(activeLayer);
    const plugin = useMemo(() => editor.getInstance<ImageEffectPlugin>('ImageEffectPlugin'), [editor]);

    useEffect(() => {
        const handler = editor.services.frame.onActiveLayerDataUpdate((layer, pickLayer) => {
            const operateLayer = pickLayer || layer;
            if (latestLayer.current !== operateLayer && operateLayer) {
                const currentActiveLayer = cloneDeep(operateLayer);
                (currentActiveLayer.data as IImageLayerProps).effect = (
                    originLayer.current?.data as IImageLayerProps
                )?.effect;
                (currentActiveLayer.data as IImageLayerProps).borderRadius = (
                    originLayer.current?.data as IImageLayerProps
                )?.borderRadius;
                originLayer.current = currentActiveLayer;
            }
            // console.log('ot active onChange', latestLayer.current === operateLayer);
            setActiveDataId(pickLayer?.id || layer?.id || null);
        });
        return () => {
            editor.off(handler);
        };
    }, [editor, latestLayer]);

    useEffect(() => {
        if (activeLayerId) {
            plugin.open(activeLayerId);
            const layer = editor.services.flatLayer.getLayer(activeLayerId, true);
            originLayer.current = cloneDeep(layer);
            setActiveData(layer);
        }
        return () => {
            if (activeLayerId) {
                void plugin.apply().catch(console.error);
            }
        };
    }, [activeLayerId, editor.services.flatLayer, plugin]);

    const updateActiveData = (layer: ILayerProps | null, options: IUpdateImageEffectDraftOptions = {}) => {
        setActiveData(layer);
        if (layer) {
            void plugin.updateDraft(cloneDeep(layer), options).catch(console.error);
        }
    };

    return (
        <ActiveDataContext.Provider value={{ activeLayer, setActiveData: updateActiveData }}>
            {children}
        </ActiveDataContext.Provider>
    );
};
